import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { 
  Row, 
  Col, 
  Typography, 
  Button,
  Space,
  message,
  Card,
  Spin,
  Alert
} from 'antd';
import {
  HeartOutlined,
  ArrowLeftOutlined,
  UserOutlined
} from '@ant-design/icons';
import { COLORS } from '../../constants/colors';
// AdminLayout now provided at router level
import PatientService from '../../services/PatientService';
import ScreeningDataForm from '../../components/ScreeningDataForm';
import { ROUTES } from '../../constants/routes';
import {
  PageContainer,
  PatientInfoCard,
  FormContainer
} from './styled';

const { Title, Text } = Typography;

const ScreeningEntry = observer(() => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPatientData();
  }, [patientId]);

  const loadPatientData = async () => {
    try {
      setLoading(true);
      // In a real implementation, this would fetch patient data from the API
      // For now, we'll use mock data or fetch from the queue
      const response = await PatientService.getPatientDetails(patientId);
      setPatient(response.data);
    } catch (error) {
      console.error('Error loading patient data:', error);
      setError('Failed to load patient information');
      message.error('Failed to load patient information');
    } finally {
      setLoading(false);
    }
  };
  const handleSaveScreeningData = async (screeningData) => {
    try {
      setSaving(true);
      
      // Save the screening data
      await PatientService.saveScreeningData(patientId, screeningData);
      
      // Update patient status to screening_data_completed (not fully completed yet)
      await PatientService.updateQueueStatus(patientId, { 
        status: 'screening_data_completed',
        screeningDataCompletionTime: new Date().toISOString()
      });
      
      message.success('Initial screening data saved successfully. Proceeding to full screening form...');
      
      // Navigate to the bus screening form for the same patient
      navigate(`${ROUTES.BUS_SCREENING}?patientId=${patientId}`);
    } catch (error) {
      console.error('Error saving screening data:', error);
      message.error('Failed to save screening data');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.ADMIN_PATIENT_QUEUE);
  };

  if (loading) {
    return (
        <PageContainer style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Spin size="large" />
        </PageContainer>
    );
  }

  if (error || !patient) {
    return (
        <PageContainer>
          <Alert
            message="Error"
            description={error || 'Patient not found'}
            type="error"
            action={
              <Button size="small" onClick={() => navigate(ROUTES.ADMIN_PATIENT_QUEUE)}>
                Back to Intake
              </Button>
            }
          />
        </PageContainer>
    );
  }

  return (
      <PageContainer>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={handleCancel}
                type="text"
                size="large"
              />
              <Title level={2} style={{ margin: 0, color: COLORS.textDark }}>
                <HeartOutlined style={{ color: COLORS.$mainRed, marginRight: 8 }} />
                Screening Data Entry
              </Title>
            </Space>
          </Col>
        </Row>

        {/* Patient Info Card */}
        <PatientInfoCard style={{ marginBottom: 24 }}>
          <Row gutter={24} align="middle">
            <Col flex="auto">
              <Space size="large">
                <div style={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  backgroundColor: COLORS.$primaryBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px'
                }}>
                  <UserOutlined />
                </div>
                <div>
                  <Title level={3} style={{ margin: 0, color: COLORS.textDark }}>
                    {patient.firstName} {patient.lastName}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '16px' }}>
                    Patient ID: {patient.patientId || patient._id}
                  </Text>
                  <br />
                  <Text type="secondary">
                    DOB: {patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString() : 'Not provided'} | 
                    Email: {patient.emailAddress || 'Not provided'} | 
                    Phone: {patient.phoneNumber || 'Not provided'}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Space direction="vertical" style={{ textAlign: 'right' }}>
                <Text strong style={{ color: COLORS.$primaryBlue }}>
                  Screening Status: In Progress
                </Text>
                <Text type="secondary">
                  Started: {new Date().toLocaleString()}
                </Text>
              </Space>
            </Col>
          </Row>
        </PatientInfoCard>

        {/* Screening Data Form */}
        <FormContainer>
          <Card
            title={
              <Space>
                <HeartOutlined style={{ color: COLORS.$mainRed }} />
                <span>Complete Screening Data</span>
              </Space>
            }
            bordered={false}
            style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
          >
            <ScreeningDataForm
              patient={patient}
              onSave={handleSaveScreeningData}
              onCancel={handleCancel}
              loading={saving}
            />
          </Card>
        </FormContainer>
      </PageContainer>
  );
});

export default ScreeningEntry;
